import {useCallback, useEffect, useState} from 'react';
import {ref, getDownloadURL} from 'firebase/storage';

// Public-assets-only resolver. Protected attachments use data/protectedFiles so
// a persisted Firebase download token can never bypass application authorization.
// The Storage instance is passed in so multiple apps can resolve their own public
// branding assets without sharing cache entries.
//
// The cache is keyed by Storage instance first: two apps can legitimately hold the
// same path against different buckets.
const caches = new WeakMap();

const cacheFor = storage => {
    if (!caches.has(storage)) {
        caches.set(storage, new Map());
    }

    return caches.get(storage);
};

export const fetchStorageUrl = (storage, path) => {
    if (!storage || !path) return Promise.resolve(null);

    const cache = cacheFor(storage);

    if (!cache.has(path)) {
        cache.set(path, getDownloadURL(ref(storage, path)).catch(e => {
            // Don't poison the cache on transient failures.
            cache.delete(path);
            throw e;
        }));
    }

    return cache.get(path);
};

// Resolve a display URL for an explicitly public image, preferring its persisted
// URL and falling back to the public Storage path when needed.
//
// A persisted URL is trusted until the browser says otherwise. The returned
// `onError` — wire it to the rendered <img>'s error event — records the URL that
// failed to load so the effect below re-resolves `path` live instead. Without it
// a dead persisted URL is permanently fatal and invisible because <img> swallows
// the failure. Public Storage resolution recovers the current asset URL.
export const useStorageUrl = (storage, cachedUrl, path) => {
    const [deadUrl, setDeadUrl] = useState(null);
    const trustedUrl = cachedUrl && cachedUrl !== deadUrl ? cachedUrl : null;

    const [url, setUrl] = useState(trustedUrl);
    const [loading, setLoading] = useState(!trustedUrl && !!path && !!storage);

    useEffect(() => {
        if (trustedUrl) {
            setUrl(trustedUrl);
            setLoading(false);
            return undefined;
        }

        if (!path || !storage) {
            setUrl(null);
            setLoading(false);
            return undefined;
        }

        let active = true;
        setLoading(true);
        fetchStorageUrl(storage, path)
            .then(u => { if (active) setUrl(u); })
            .catch(e => {
                console.warn(e);
                // Nothing left to show: whatever was persisted is either absent
                // or already proven dead, so don't keep a broken image on screen.
                if (active) setUrl(null);
            })
            .finally(() => { if (active) setLoading(false); });

        return () => { active = false; };
    }, [storage, trustedUrl, path]);

    // Only ever blames the *persisted* URL, so the live-resolved replacement
    // failing too can't loop: the state is already set to that same value.
    const onError = useCallback(() => {
        if (cachedUrl) {
            setDeadUrl(cachedUrl);
        }
    }, [cachedUrl]);

    return {url, loading, onError};
};
